import { h, toRaw, isProxy } from 'vue';
import { LoggerService } from '@magnolia/template-annotations';
import { TEMPLATE_ID_PROP } from './constants';
import DefaultFallbackComponent from './DefaultFallbackComponent';

export const insertComments = (node, commentOpen, commentClose) => {
  if (node) {
    node.parentNode.insertBefore(document.createComment(commentOpen), node);
    node.parentNode.insertBefore(document.createComment(commentClose), node.nextSibling);
  }
};

function removeCommentElement(element) {
  if (element && element.nodeType === 8) {
    element.remove();
  }
}

export const removeComments = (node) => {
  if (!node) {
    return;
  }
  removeCommentElement(node.previousSibling);
  removeCommentElement(node.nextSibling);
};

export const buildKey = (componentContent) => {
  return componentContent['@name'];
};

export const getComponentProperties = (componentContent, index, isFallbackComponent) => {
  if (!componentContent || typeof componentContent !== 'object') {
    return {};
  }

  const props = {};
  let propsContainer;
  if (isFallbackComponent) {
    props.data = {};
    propsContainer = props.data;
  } else {
    propsContainer = props;
  }
  props.metadata = { '@index': index };
  props.key = buildKey(componentContent);

  Object.keys(componentContent).forEach((key) => {
    if (key.match(/^(@|mgnl:|jcr:)/)) {
      props.metadata[key] = componentContent[key];
    } else {
      propsContainer[key.replace('-', '_')] = componentContent[key];
    }
  });
  return props;
};

export const getRenderedComponent = (
  componentContent,
  componentMappings,
  fallbackComponentConfig,
  childNodes,
  index
) => {
  const emptyComponent = null;
  if (!componentContent) {
    const msg = 'Component content is missing';
    LoggerService.error(msg);
    return emptyComponent;
  }
  if (!componentMappings) {
    const msg = 'Component mappings is missing';
    LoggerService.error(msg);
    return emptyComponent;
  }
  let componentClass = componentMappings[componentContent[TEMPLATE_ID_PROP]];
  let isFallbackComponent = false;
  if (!componentClass) {
    const msg = `Component with ID ${componentContent[TEMPLATE_ID_PROP]} is not mapped.`;
    LoggerService.error(msg);
    const fallbackComponent = getFallbackComponent(fallbackComponentConfig);
    if (!fallbackComponent) {
      return emptyComponent;
    }
    componentClass = fallbackComponent;
    isFallbackComponent = true;
  }
  const props = getComponentProperties(componentContent, index, isFallbackComponent);
  const component = isProxy(componentClass) ? toRaw(componentClass) : componentClass;
  return h(component, props, childNodes);
};

function getFallbackComponent(fallbackComponentConfig) {
  const fallbackComponent =
    typeof fallbackComponentConfig === 'function'
      ? fallbackComponentConfig()
      : fallbackComponentConfig;
  return fallbackComponent != null && typeof fallbackComponent !== 'boolean'
    ? fallbackComponent
    : fallbackComponent
    ? DefaultFallbackComponent
    : null;
}

export const mgnlRender = (
  componentContent,
  componentMappings,
  fallbackComponentConfig,
  childNodes,
  index = 0
) => {
  return getRenderedComponent(
    componentContent,
    componentMappings,
    fallbackComponentConfig,
    childNodes,
    index
  );
};
